import { Component, ElementRef, OnDestroy, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import { NgSelectComponent } from "@ng-select/ng-select";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
  ToastController,
} from "ionic-angular";
import { DropdownItem } from "models/order-management/dropdownItem";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import CredentialedResource from "models/resource-management/credentialed-resource";
import { ScriptSureDrug } from "models/scriptSure/scriptsureDrug";
import {
  isOtherDose,
  OtherDose,
  ScriptSureDrugDoseResponse,
} from "models/scriptSure/scriptsureDrugDoseResponse";
import { ScriptSureDrugsRequest } from "models/scriptSure/scriptsureDrugsRequest";
import { ScriptSureDrugsResponse } from "models/scriptSure/scriptsureDrugsResponse";
import SelectFormInput from "models/SelectFormInput";
import * as moment from "moment-timezone";
import { LastFirstPatientName } from "pipes/output-formatters/last-first-patient-name";
import { DocManagementDataService } from "providers/doc-management-data-service/doc-management-data-service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { PatientMedicationsService } from "providers/patient-charts/patient-medications.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Observable } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  mergeMap,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import { Subscription } from "rxjs/Subscription";
import Utils from "utils/Utilities";
import ContentWrapper from "../../../../models/content-wrapper/content-wrapper";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import CollapsibleEvent from "../../../../models/events/collapsible-modal.event";
import { OrderComponent } from "../../../../models/order-management/orderComponent";
import { Patient } from "../../../../models/patient-chart/patient";
import CompanyResourceSummary from "../../../../models/resource-management/company-resource-summary";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import { MedicationDataService } from "../../../../providers/order-data-service/medications-data-service";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import {
  CARE_TYPE_HOSPICE,
  PATIENT_STATUS_ON_SERVICE,
} from "../../../../utils/meds-constants";
import {
  MedicationLookup,
  MedicationSearchResults,
  PatientMedication,
} from "../patient-medication";

@Component({
  selector: "medication-edit",
  templateUrl: "medication-edit.html",
})
export default class MedicationEditPage implements OnDestroy {
  @ViewChild("hiddenInputMedicationName") hiddenInputMedicationName: {
    setFocus: () => void;
  };
  @ViewChild("medicationNameId") medicationNameInput: NgSelectComponent;
  @ViewChild("hiddenInputOrderingProvider") hiddenInputOrderingProvider: {
    setFocus: () => void;
  };
  @ViewChild("orderingProviderId") orderingProviderInput: NgSelectComponent;
  mustConvertToOrder: boolean = false;
  saveLabel: string = "Save";
  showOrderDetails: boolean = false;
  medicationForm: FormGroup;
  orderDetails: FormGroup;
  subscriptions: Subscription[] = [];
  medicationsScriptsure$: Observable<Array<ScriptSureDrug>> = of([]);
  medicationInput$: Subject<string> = new Subject();
  medicationsLoading = false;
  strengthOptions: (ScriptSureDrugDoseResponse | OtherDose)[] = [];
  isOrder: boolean = false;
  canShowOrder: boolean = false;
  configurations: any = [];
  providers: CompanyResourceSummary[] = [];
  providerSearchSubject: Subject<string> = new Subject();
  patientChart: Patient;
  credentialedResource: CredentialedResource;
  userDisplayName: string;
  duplicatedMed: boolean = false;
  hospicePaysOrderOptions: SelectFormInput[] = [
    { display: "Yes", value: "Yes" },
    { display: "No", value: "No" },
  ];
  drug: ScriptSureDrug;
  categoryDropdownItems: DropdownItem[] = [];
  categoryOtherDescription: string;

  headerLabel: string = "Add Intake Medication";
  patientChartId: number;

  maxDate = new Date();
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
  minDateFormat = {
    year: this.minDate.getFullYear(),
    month: this.minDate.getMonth() + 1,
    day: this.minDate.getDate(),
  };
  maxDateFormat = {
    year: this.maxDate.getFullYear(),
    month: this.maxDate.getMonth() + 1,
    day: this.maxDate.getDate(),
  };
  medication: PatientMedication;

  spinner: any = null;
  spinnerCounter: number = 0;

  datePlacementPreference: PlacementArray = ["bottom-left"];

  changeOrderSource = false;
  showOtherInput = false;
  options: DropdownItem[] = [];
  showVerified = false;
  showFieldUpload = false;
  selectedOrderApprovalType: string;
  static verifiedOrderOptions: SelectFormInput[] = [
    { display: "Verbal", value: "verbal" },
    { display: "Written", value: "written" },
  ];
  verifiedOrderOptions = MedicationEditPage.verifiedOrderOptions;
  providerQuery$ = new Subject<string>();
  providerDropdownItems$: Observable<DropdownItem[]>;

  medicationSearchResults: MedicationSearchResults;
  providerSelected = {
    label: "",
    value: 0,
  };

  searchTerm$: Subject<string> = new Subject<string>();
  medicationScriptSure: ScriptSureDrug;
  medicationVendorReplacement: boolean = false;

  medications: MedicationLookup[] = [];
  searchSubject: Subject<string> = new Subject();
  dosageStrengths: { label: string; value: string }[] = [];
  dosageOptions: string[] = [];
  frequencyOptions: string[] = [];
  isEdit: boolean = false;
  strengthScriptSure: ScriptSureDrugDoseResponse | OtherDose;
  medicationDoses$: Observable<ScriptSureDrugDoseResponse[]>;
  isFreeText: boolean = false;

  @ViewChild("inputFile") inputFile: ElementRef;
  listFiles = [];
  idFiles: string[] = [];
  numberOfFiles = 0;
  dataTransfer = new DataTransfer();
  showToaster = false;

  public constructor(
    private fb: FormBuilder,
    public navCtrl: NavController,
    private navParams: NavParams,
    private alertController: AlertController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private medicationService: PatientMedicationsService,
    private resourceManagementService: ResourceManagementService,
    public stateData: StateManagementService,
    private medicationDataService: MedicationDataService,
    public utilityService: UtilityService,
    private navBarService: NavBarServiceProvider,
    private dataService: DocManagementDataService
  ) {
    this.isEdit = this.navParams.data.medication;
    this.patientChart = this.navParams.data.patientChart;
    this.patientChartId = this.navParams.data.patientChartId;
    this.medicationSearchResults = this.navParams.data.medicationSearchResults;
    this.canShowOrder =
      this.navParams.data.showOrderManagement &&
      !(
        this.stateData.patientCareType &&
        this.stateData.patientCareType.toLowerCase() == "palliative"
      );
    this.mustConvertToOrder = this.navParams.data.convertToOrder;
    this.credentialedResource = this.navParams.data.credentialedResource;
    this.userDisplayName = this.navParams.data.userDisplayName;

    this.medicationForm = new FormGroup({
      name: this.fb.control("", Validators.required),
      dosageForm: this.fb.control(""),
      strength: this.fb.control("", [Validators.required, this.cannotBeNA()]),
      route: this.fb.control("", [Validators.required, this.cannotBeNA()]),
      frequency: this.fb.control("", [Validators.required, this.cannotBeNA()]),

      dosage: this.fb.control("", [Validators.required, this.cannotBeNA()]),
      symptoms: this.fb.control(""),
      rnNotes: this.fb.control([]),
      isControlledSubstance: this.fb.control(false),
      isCompound: this.fb.control(false),
      isOrder: this.fb.control(false),
      externalId: this.fb.control(""),
    });

    let patientCareType = this.navParams.data.patientChart.careType;
    let patientChartStatus =
      this.navParams.data.patientChart.patientChartStatus;

    this.changeOrderSource =
      patientCareType == CARE_TYPE_HOSPICE &&
      patientChartStatus == PATIENT_STATUS_ON_SERVICE;

    if (this.navParams.data.medication) {
      this.headerLabel = "Edit Intake Medication";
      this.medication = this.navParams.data.medication;
      this.isFreeText = this.navParams.data.medication?.isFreeText ?? false;
      this.medicationsScriptsure$ = of([]);
      this.setEditValues();
      this.medicationForm.patchValue(this.medication);
    }
    this.setupProviderSearch();
    this.subscriptions.push(
      this.medicationForm.controls["isOrder"].valueChanges.subscribe(
        (x: string) => this.handleIsOrder(x)
      )
    );

    this.medicationForm.get("isOrder").setValue(this.mustConvertToOrder);
  }
  getMedicationDoses() {
    this.medicationForm.controls["strength"].reset();
    this.medicationDoses$ = this.medicationDataService.getMedicationDoses(
      this.drug.ROUTED_MED_ID
    );
    this.medicationDoses$.subscribe(
      (response: ScriptSureDrugDoseResponse[]) => {
        this.strengthOptions = response;
      },
      (error) => {
        console.error("Error when searching for medication options", error);
      }
    );
  }
  /**
   * ngOnDestroy -- clean up things that need to be cleaned up ... like subscriptiopns
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  get isOtherSelected() {
    return this.showOtherInput;
  }

  cannotBeNA() {
    //validator used to ensure that some fields cannot be 'N/A'
    const naRegex = /^n\/?a$/i;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isNA = naRegex.test(control.value);
      return isNA ? { cannotBeNA: { value: control.value } } : null;
    };
  }

  ngOnInit() {
    this.formatCategory(this.navParams.data.categories);
    this.medicationForm.controls["name"].valueChanges.subscribe((x) => {
      this.medicationForm.updateValueAndValidity();
      this.checkDuplicatedMed();
    });
    this.medicationsScriptsure$ = this.medicationInput$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      tap(() => (this.medicationsLoading = true)),
      switchMap((term) => {
        // null -> clear, '' -> field cleared by backspace
        if (!term || term.length === 0) {
          return of([]);
        }
        return this.medicationDataService
          .getMedications(this.searchInputToScriptSureSearchRequest(term))
          .pipe(
            mergeMap((scriptSureDrugsResponse) =>
              of(scriptSureDrugsResponse.drugs)
            ),
            catchError(() => of([])), // empty list on error
            tap(() => (this.medicationsLoading = false))
          );
      })
    );
    this.providerSearch();
  }

  getLabel(dropdownItem: DropdownItem): string {
    if (
      dropdownItem.value == "Other" &&
      this.orderDetails.controls["orderCategoryOther"].value
    ) {
      return `Other (${this.orderDetails.controls["orderCategoryOther"].value})`;
    }
    return dropdownItem.label;
  }

  onChangeCategory(event: string[]) {
    if (event) {
      const values = Object.values(event);
      this.orderDetails.controls["orderCategory"].value.concat(event);
    }
  }
  onChangeOtherCategory(event: any) {
    if (event) {
      this.orderDetails.controls["orderCategoryOther"].setValue(
        event.target.value
      );
    }
  }

  ngOnClickMedicationName() {
    this.hiddenInputMedicationName.setFocus();
    this.medicationNameInput.filterInput.nativeElement.focus();
  }

  ngOnClickOrderingProvider() {
    this.hiddenInputOrderingProvider.setFocus();
    this.orderingProviderInput.filterInput.nativeElement.focus();
  }

  handleIsOrder(i: string) {
    if (i) {
      this.orderDetails = this.fb.group({
        orderCategory: this.fb.control(""),
        orderCategoryOther: this.fb.control(""),
        orderStartDate: this.fb.control(
          moment().format("MM/DD/YYYY"),
          Validators.required
        ),
        orderReadBackAndVerified: this.fb.control(undefined),
        orderDiscontinueDate: this.fb.control(""),
        orderProviderNotes: this.fb.control([]),
        orderHospicePays: this.fb.control([], Validators.required),
        orderSelectedOption: this.fb.control(""),
        orderApprovalType: this.fb.control([], Validators.required),
        orderDocumentIds: this.fb.control(""),
        orderOrderingProviderId: this.fb.control(null, Validators.required),
        orderOrderingProviderDisplayName: this.fb.control(
          "provider.orderOrderingProviderDisplayName"
        ),
      });

      this.orderDetails.controls["orderCategory"].valueChanges.subscribe(
        (value) => {
          if (value.includes("other")) {
            this.showOtherInput = true;
          } else {
            this.showOtherInput = false;
          }
        }
      );

      if (this.providerSelected.value > 0) {
        this.orderDetails.controls["orderOrderingProviderId"].setValue(
          this.providerSelected.value
        );
      }

      this.saveLabel = "Save and Create Order";
      this.isOrder = true;
      this.medicationForm.addControl("orderDetails", this.orderDetails);
      this.showOrderDetails = true;
      this.orderDetails.controls[
        "orderReadBackAndVerified"
      ].valueChanges.subscribe((val) => {
        this.orderDetails.updateValueAndValidity();
      });

      this.orderDetails.controls["orderApprovalType"].valueChanges.subscribe(
        (val) => {
          this.selectedOrderApprovalType = val;
          if (val === "verbal") {
            this.orderDetails.controls[
              "orderReadBackAndVerified"
            ].setValidators(Validators.required);
            this.orderDetails.updateValueAndValidity();
            this.showVerified = true;
            this.showFieldUpload = false;
          } else if (val === "written") {
            this.showFieldUpload = true;
            this.showVerified = false;
            this.orderDetails.controls[
              "orderReadBackAndVerified"
            ].clearValidators();
            this.orderDetails.updateValueAndValidity();
          } else {
            this.showFieldUpload = false;
            this.showVerified = false;
            this.orderDetails.controls[
              "orderReadBackAndVerified"
            ].clearValidators();
            this.orderDetails.updateValueAndValidity();
          }
          this.orderDetails.controls["orderReadBackAndVerified"].setValue(
            undefined
          );
        }
      );

      // Handle setting fields if user is a provider
      if (isProfessionalProvider(this.credentialedResource)) {
        const providerDropdownItem: DropdownItem = {
          label: this.credentialedResource.findDisplayName(new Date()),
          value: `${this.credentialedResource.externalId}`,
        };
        this.orderDetails.controls["orderOrderingProviderId"].setValue(
          providerDropdownItem.value
        );
        this.orderDetails.controls["orderOrderingProviderId"].disable();
        this.onProviderSelected(providerDropdownItem);
        this.orderDetails.controls["orderApprovalType"].setValue("written");
      }
    } else {
      this.isOrder = false;
      this.saveLabel = "Save";
      if (this.medicationForm.contains("orderDetails")) {
        this.medicationForm.removeControl("orderDetails");
        this.showOrderDetails = false;
        this.orderDetails = null;
      }
    }
  }

  handleStrengthSelected(
    strengthScriptSure: ScriptSureDrugDoseResponse | OtherDose
  ): void {
    if (!strengthScriptSure) {
      return;
    }
    this.strengthScriptSure = strengthScriptSure;
    this.medicationForm.controls["strength"].setValue(
      this.getStrength(this.strengthScriptSure)
    );
    this.medicationForm.controls["dosageForm"].setValue(
      this.strengthScriptSure.MED_DOSAGE_FORM_DESC,
      { emitEvent: false }
    );
  }

  onMedicationScriptsureSelected(
    drug: ScriptSureDrug,
    isUpdate?: boolean
  ): void {
    if (!drug) {
      return;
    }
    this.duplicatedMed = false;
    this.drug = drug;
    this.checkDuplicatedMed();
    this.getMedicationDoses();
  }

  get notFoundText(): string {
    return this.medicationsLoading
      ? "Searching medications..."
      : "No medications found.";
  }

  searchInputToScriptSureSearchRequest(input: string): ScriptSureDrugsRequest {
    return {
      term: input,
      searchMedication: true,
      searchIndication: false,
      searchOtc: true,
      searchSupply: true,
      searchBrand: true,
      searchGeneric: true,
      includeCode: true,
      searchStatus: 0,
    };
  }

  checkDuplicatedMed() {
    if (this.drug) {
      this.duplicatedMed = this.medicationSearchResults.patientMedication.some(
        (medication) =>
          //Medication name is composed of med name + strength, so we need to extract the strength to compare only the medication name
          medication.name.replace(" - " + medication.strength, "") ===
            this.drug.MED_ROUTED_MED_ID_DESC &&
          medication.orderStatus === "Active"
      );
    }
  }
  categorySelected(dropdownItem: DropdownItem): boolean {
    return this.orderDetails.controls["orderCategory"].value.includes(
      dropdownItem.value
    );
  }

  setupProviderSearch(): void {
    this.providerSearchSubject
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((x: string) => {
        if (!x || x.length === 0) {
          this.providers = [];
        } else if (x.length > 2) {
          this.searchProviders(x);
        }
      });
  }

  searchProviders(search: string): void {
    this.resourceManagementService
      .getCompanyResourceSummaries(
        0,
        10,
        search ? search : undefined,
        new SortOptions("last_name", "asc"),
        CompanyResourceType.PROVIDER
      )
      .pipe(take(1))
      .subscribe((x: ContentWrapper<CompanyResourceSummary>) => {
        this.providers = x.content;
      });
  }
  discontinueDate(selectedDate): string {
    if (selectedDate) {
      const selectedDateTime = moment(selectedDate);
      return selectedDateTime.endOf("day").toISOString();
    }
    return null; // Return null if no date is selected
  }

  getStrength(
    strengthScriptSure: ScriptSureDrugDoseResponse | OtherDose
  ): string {
    if (!strengthScriptSure) {
      return "";
    }
    if (isOtherDose(strengthScriptSure)) {
      return strengthScriptSure.otherDescription;
    }
    const { MED_STRENGTH, MED_STRENGTH_UOM, MED_DOSAGE_FORM_DESC } =
      strengthScriptSure;
    if (
      MED_STRENGTH &&
      MED_STRENGTH_UOM &&
      MED_STRENGTH !== "" &&
      MED_STRENGTH_UOM !== ""
    ) {
      return `${strengthScriptSure.MED_STRENGTH} ${strengthScriptSure.MED_STRENGTH_UOM}`;
    }
    return MED_DOSAGE_FORM_DESC;
  }

  save(): void {
    let newMed = { ...this.medicationForm.value };
    for (let t of Object.keys(newMed)) {
      if (newMed[t] === "") newMed[t] = null;
    }

    // Handle strength value for free text vs regular medications
    if (this.isFreeText) {
      // For free text medications, use the strength from the form
      newMed.strength = newMed.strength || "";
    } else {
      // For regular medications, use the getStrength method
      newMed.strength = this.getStrength(this.strengthScriptSure);
    }

    // Handle name construction for free text vs regular medications
    if (this.isFreeText) {
      // For free text medications, use the name from the form
      newMed.name = newMed.name;
    } else {
      // For regular medications, construct name from drug object
      newMed.name = `${this.drug?.MED_ROUTED_MED_ID_DESC || ""} - ${
        newMed.strength
      }`;
    }

    newMed.ndc = this.strengthScriptSure?.ndc;
    newMed.rxNormal = this.strengthScriptSure?.rxNorm;
    newMed.isFreeText = this.isFreeText;

    newMed.externalPatientChartId = this.patientChart.externalId;
    newMed.convertToOrder = this.mustConvertToOrder;
    newMed.patientCareType = this.stateData.patientCareType
      ? this.stateData.patientCareType.toLowerCase()
      : "";
    if (!this.userDisplayName) {
      let toast = this.toastCtrl.create({
        message: `Unable to retrieve user info from Resources, please confirm user has been entered.`,
        duration: 3000,
        position: "below",
        cssClass: "error",
      });
      toast.present();
      return;
    }

    if (this.isOrder) {
      newMed.orderDetails = {
        ...newMed.orderDetails,
        orderStartDate: moment(newMed.orderDetails.orderStartDate)
          .startOf("day")
          .utc()
          .toISOString(),
        timezone: moment.tz.guess(),
        orderApprovalType: this.selectedOrderApprovalType,
        orderCreatedByDisplayName: this.userDisplayName,
        orderOrderingProviderId: parseInt(
          this.orderDetails.controls["orderOrderingProviderId"].value
        ),
        orderPatientName: new LastFirstPatientName().transform({
          firstName: this.patientChart.firstName,
          lastName: this.patientChart.lastName,
          prefix: null,
          middleInitial: this.patientChart.middleInitial,
          suffix: this.patientChart.suffix
            ? this.patientChart.suffix.description
            : null,
        }),
      };
      if (
        newMed.orderDetails.orderDiscontinueDate !== null &&
        newMed.orderDetails.orderDiscontinueDate !== ""
      ) {
        newMed.orderDetails = {
          ...newMed.orderDetails,
          timezone: moment.tz.guess(),
          orderDiscontinueDate: this.discontinueDate(
            newMed.orderDetails.orderDiscontinueDate
          ),
          orderDiscontinueProviderId: parseInt(
            this.orderDetails.controls["orderOrderingProviderId"].value
          ),
          orderDiscontinueProviderDisplayName:
            this.orderDetails.controls["orderOrderingProviderDisplayName"]
              .value,
        };
      } else {
        newMed.orderDetails = {
          ...newMed.orderDetails,
          orderDiscontinueDate: null,
        };
      }
      newMed.orderDetails.teamId = this.navParams.data.teamId;
      newMed.orderDetails.teamQbosId = this.navParams.data.teamQbosId;
      newMed.orderDetails.teamName = this.navParams.data.teamName;
      newMed.orderDetails.orderHospicePays =
        newMed.orderDetails.orderHospicePays == "Yes" ? true : false;
    }

    newMed.changeOrderSource = this.changeOrderSource;

    if (newMed.isOrder) {
      newMed.convertToOrder = true;
    }

    this.increaseSpinnerCount("Saving ...");
    if (
      this.medication &&
      this.medication.entityId &&
      this.medication.entityId !== ""
    ) {
      this.medicationService
        .updateMedication(this.patientChartId, this.medication.entityId, newMed)
        .pipe(take(1))
        .subscribe(
          (x) => {
            this.decreaseSpinnerCount();
            this.navCtrl.pop();
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.presentToast("Error saving medication");
          }
        );
    } else {
      if (newMed.convertToOrder) {
        this.uploadFile(newMed);
      } else {
        this.createMedication(newMed);
      }
    }
  }

  createMedication(newMed: any) {
    this.medicationService
      .createMedication(this.patientChartId, newMed)
      .pipe(take(1))
      .subscribe(
        (x) => {
          this.decreaseSpinnerCount();
          this.navCtrl.pop();
        },
        (err) => {
          this.decreaseSpinnerCount();
          this.presentToast("Error saving medication");
        }
      );
  }

  cancel(): void {
    /**
     * Handles the cancel event
     */
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present().then((r) => console.log("Handled"));
  }

  presentToast(message) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "error",
    });

    toast.onDidDismiss(() => {});

    toast.present();
  }

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      if (this.spinner) {
        this.spinner.dismiss();
      }
      this.spinner = null;
    }
  }

  public onCollapseClicked($e: CollapsibleEvent): void {
    if ($e.controlId === "order-details") {
      this.showOrderDetails = $e.isOpen;
    }
  }

  formatCategory(c: OrderComponent) {
    this.categoryDropdownItems = c.properties[0].dropdownItems;
  }

  providerSearch() {
    if (isProfessionalProvider(this.credentialedResource)) {
      this.providerDropdownItems$ = of([
        {
          label: this.credentialedResource.findDisplayName(new Date()),
          value: `${this.credentialedResource.externalId}`,
        },
      ]);
      return;
    }
    this.providerDropdownItems$ = this.providerQuery$.pipe(
      distinctUntilChanged(),
      filter((query) => !query || ![1, 2].includes(query.length)),
      switchMap((query: string | null) => {
        // null -> clear, '' -> field cleared by backspace
        if (!query || query.length === 0) {
          return of([]);
        }
        return this.resourceManagementService
          .getCompanyResourceSummaries(
            0,
            100,
            query ? query : undefined,
            new SortOptions("last_name", "asc"),
            CompanyResourceType.PROVIDER
          )
          .pipe(
            mergeMap((providerList) =>
              of(
                providerList.content.map((provider) => {
                  return {
                    label: provider.displayName,
                    value: `${provider.externalId}`,
                  };
                })
              )
            ),
            catchError(() => of([]))
          );
      })
    );
  }
  onProviderSelected(event: DropdownItem) {
    if (event) {
      this.orderDetails.controls["orderOrderingProviderDisplayName"].setValue(
        event.label
      );
      this.providerSelected.label = event.label;
      this.providerSelected.value = parseInt(event.value);
    }
  }
  setEditValues() {
    // If the medication is free text, we skip the Scriptsure lookup as for some medications there won't be a match
    if (this.isFreeText) {
      return;
    }
    //Medication name is composed of med name + strength, so we need to extract the strength to compare only the medication name
    const medicationName = this.medication.name.replace(
      " - " + this.medication.strength,
      ""
    );
    this.medicationDataService
      .getMedications(this.searchInputToScriptSureSearchRequest(medicationName))
      .subscribe(
        (response: ScriptSureDrugsResponse) => {
          this.medicationInput$.next(medicationName);
          const med = response.drugs.find(
            (med) => med.MED_ROUTED_MED_ID_DESC === medicationName
          );
          this.drug = med;
          // ScriptSure may not return an exact match for a previously-saved
          // medication (renamed/removed drug, formatting drift). When `med` is
          // undefined, onMedicationScriptsureSelected() short-circuits and
          // never sets medicationDoses$, so subscribing to it — and reading
          // med.MED_NAME_ID — would throw and blow up the Edit modal. Fall back
          // to the strength already on the medication and keep the saved name.
          this.onMedicationScriptsureSelected(med);
          if (!med) {
            this.handleStrengthSelected({
              MED_MEDID_DESC: this.medication.strength,
              otherDescription: this.medication.strength,
              MED_DOSAGE_FORM_DESC: undefined,
              ndc: undefined,
              rxNorm: undefined,
            });
            this.medicationForm.controls["name"].setValue(this.medication.name);
            return;
          }
          // Get the doses for the medication and then subscribe to the results to find the dose
          this.medicationDoses$.subscribe((doses) => {
            let dose: ScriptSureDrugDoseResponse | OtherDose = doses.find(
              (dose) => this.getStrength(dose) === this.medication.strength
            );
            if (!dose) {
              dose = {
                MED_MEDID_DESC: this.medication.strength,
                otherDescription: this.medication.strength,
                MED_DOSAGE_FORM_DESC: undefined,
                ndc: undefined,
                rxNorm: undefined,
              };
            }
            this.handleStrengthSelected(dose);
          });
          this.medicationForm.controls["name"].setValue(med.MED_NAME_ID);
        },
        (error) => {
          console.error("Error when searching for medication options", error);
        }
      );
  }

  addOtherStrength = (addTagText: string) => {
    this.strengthOptions = [
      ...this.strengthOptions,
      {
        MED_MEDID_DESC: addTagText,
        otherDescription: addTagText,
        MED_DOSAGE_FORM_DESC: undefined,
        ndc: undefined,
        rxNorm: undefined,
      },
    ];
  };

  get orderApprovalTypeHidden() {
    return isProfessionalProvider(this.credentialedResource);
  }

  selectFile(e) {
    const { files } = e.target;
    for (let i = 0; i < files.length; i++) {
      if (files[i].size < 10485760) {
        this.listFiles.push(files[i]);
        this.dataTransfer.items.add(e.target.files[i]);
      } else {
        this.showToaster = true;
      }
    }
    if (this.showToaster) {
      this.utilityService.presentCustomErrorToast(
        "Some files exceed the size limit."
      );
      this.showToaster = false;
    }
    e.target.files = this.dataTransfer.files;
  }

  deleteFile(event: any, index: number) {
    this.dataTransfer.items.remove(index);
    const newFileList = [];
    this.inputFile.nativeElement.files = this.dataTransfer.files;
    for (let i = 0; i < this.listFiles.length; i++) {
      newFileList.push(this.dataTransfer.files[i]);
    }
    this.listFiles = newFileList;
  }

  async uploadFile(newMed: any) {
    const internalListFiles = this.listFiles.filter((x) => x !== undefined);
    if (internalListFiles.length != 0) {
      await Promise.all(
        this.listFiles
          .filter((x) => x !== undefined)
          .map(async (file, index) => {
            const counter = internalListFiles.length;
            let payload = {
              key: this.navBarService.getExternalId(),
              label: Utils.getFileName(file.name),
              serviceName: "PatientChart",
              fileExtension: file.name.slice(file.name.lastIndexOf(".")),
              description: "Order File",
              category: "Provider Orders",
              patientID: this.navBarService.getExternalId(),
            };
            let body = new FormData();
            body.append("file", file);
            body.append("tags", JSON.stringify(payload));
            await this.dataService.uploadDocument(body).subscribe(
              (data) => {
                this.idFiles.push(data["documentId"].toString());
                this.numberOfFiles++;
                if (this.numberOfFiles == counter) {
                  newMed.orderDetails = {
                    ...newMed.orderDetails,
                    orderDocumentIds: this.idFiles,
                  };
                  this.createMedication(newMed);
                  this.listFiles = [];
                }
              },
              (err) => {
                this.numberOfFiles++;
                if (this.numberOfFiles == counter) {
                  newMed.orderDetails = {
                    ...newMed.orderDetails,
                    orderDocumentIds: this.idFiles,
                  };
                  this.createMedication(newMed);
                  this.listFiles = [];
                }
              }
            );
          })
      );
    } else {
      this.createMedication(newMed);
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/medications/medication-edit/medication-edit.ts